/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";

import { useEffect, useState } from "react";

import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import { useHomeRoute } from "@/routes/hooks/useHomeRoute";
import Drawer from "@mui/material/Drawer";
import Typography from "@mui/material/Typography";

import { colors } from "@dotnet/telematics-design-system";

import CloseButton from "../../../shared/components/buttons/CloseButton";
import GoBackButton from "../../../shared/components/buttons/GoBackButton";
import SettingsButton from "../../../shared/components/buttons/SettingsButton";
import { useReleaseFeatureToggle } from "../../../shared/featureToggles/hooks";
import { FEATURE_NAMES } from "../../../shared/featureToggles/types";
import { useStyles as sharedStyles } from "../../../shared/styles/sharedStyles";
import Logo from "../../Logo/Logo";
import Controls from "../../Navbar/components/Controls";
import Notifications from "../../Navbar/components/Notifications/Notifications";
import useTutorialMode from "../../TutorialMode/hooks/useTutorialMode";
import Navigation from "../Navigation/Navigation";
import { useStyles } from "../styles";

interface MobileDrawerProps {
  isOpen: boolean;
  setIsMobileDrawerOpen: (isOpen: boolean) => void;
}

const MobileDrawer = ({ isOpen, setIsMobileDrawerOpen }: MobileDrawerProps) => {
  const {
    isTutorialActive,
    isTutorialOpen,
    stepIndex,
    stepGroup,
    setTutorialDetails
  } = useTutorialMode();
  const { isReleaseFeatureEnabled } = useReleaseFeatureToggle();
  const { t } = useTranslation();

  const navigate = useNavigate();

  const { homeRoute } = useHomeRoute();

  const { drawerDiv, initialDrawerDiv, navbarControls } = useStyles();
  const { logo } = sharedStyles();

  const [isInitialView, setIsInitialView] = useState(true);

  useEffect(() => {
    isTutorialOpen && !isOpen && handleDrawerOpenFalse();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTutorialOpen, stepGroup, stepIndex]);

  const handleInitialView = (val: boolean) => setIsInitialView(val);

  const handleDrawerOpenFalse = () => {
    setIsMobileDrawerOpen(false);
  };

  const handleOnEntered = () =>
    isTutorialActive &&
    setTutorialDetails({
      isTutorialOpen: true,
      stepIndex: stepIndex,
      stepGroup: stepGroup,
      isTutorialActive: true
    });

  return (
    <Drawer
      data-testid="mobile-drawer"
      anchor="top"
      open={isOpen}
      onClose={handleDrawerOpenFalse}
      slotProps={{
        transition: {
          onExited: () => handleInitialView(true),
          onEntered: handleOnEntered
        }
      }}
      sx={{
        "& .MuiDrawer-paper": {
          height: "100%"
        }
      }}
    >
      <div css={css(drawerDiv)}>
        <Logo css={css(logo)} onClick={() => navigate(homeRoute)} />
        <CloseButton onClose={handleDrawerOpenFalse} />
      </div>

      {isInitialView ? (
        <div css={css(initialDrawerDiv)}>
          <Typography
            variant="body3Medium"
            css={css({
              color: colors.textPlaceholder,
              marginBottom: "4px",
              letterSpacing: "0.4px"
            })}
          >
            {t("General##menu")}
          </Typography>
          <Navigation />

          <div css={css(navbarControls)}>
            {isReleaseFeatureEnabled(FEATURE_NAMES.ShowNotifications) && (
              <Notifications />
            )}
            <SettingsButton handleInitialView={handleInitialView} />
          </div>
        </div>
      ) : (
        <>
          <GoBackButton onButtonClick={() => handleInitialView(true)} />
          <Controls handleDrawer={setIsMobileDrawerOpen} />
        </>
      )}
    </Drawer>
  );
};

export default MobileDrawer;
